import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/common/ui/alert-dialog";
import { Input } from "@/common/ui/input";

import type { FeatureFlag, TargetingRule, UpdateFlagInput } from "./feature-flags-types";
import { classifyServeRule } from "./serve-rule";

export interface ServeValueEdit {
  readonly variationKey: string;
  readonly variationValue: unknown;
}

interface EditFlagDialogProps {
  flag: FeatureFlag | null;
  /**
   * The flag's current rules, or `null` while they are still loading. Needed
   * to tell an editable "serve this when enabled" rule from real targeting the
   * form must not overwrite.
   */
  rules: readonly TargetingRule[] | null;
  /** The rules fetch failed. The serve control is withheld rather than guessing. */
  rulesError?: boolean;
  /** A failure from the save itself, shown here because the page banner sits behind this overlay. */
  saveError?: string | null;
  /**
   * A save is in flight. Blocks Save and Cancel: leaving Cancel live let the
   * operator open a different flag while a write was still running, and the
   * result then landed on whichever flag was open when it returned.
   */
  saving?: boolean;
  open: boolean;
  onConfirm: (key: string, input: UpdateFlagInput, serve: ServeValueEdit | null) => void;
  onCancel: () => void;
}

// JSON-encode the variation value for the editor so non-string values
// (boolean, number, object) round-trip cleanly. "off : false" not "off : ".
function encodeValue(value: unknown): string {
  return JSON.stringify(value);
}

function decodeValue(raw: string): { ok: true; value: unknown } | { ok: false } {
  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch {
    return { ok: false };
  }
}

export function EditFlagDialog({
  flag,
  rules,
  rulesError = false,
  saveError = null,
  saving = false,
  open,
  onConfirm,
  onCancel,
}: EditFlagDialogProps): React.ReactElement | null {
  const [description, setDescription] = useState("");
  const [defaultKey, setDefaultKey] = useState("");
  const [defaultValueRaw, setDefaultValueRaw] = useState("");
  const [keyError, setKeyError] = useState<string | null>(null);
  const [valueError, setValueError] = useState<string | null>(null);
  const [serveKey, setServeKey] = useState("on");
  const [serveValueRaw, setServeValueRaw] = useState("true");
  const [serveError, setServeError] = useState<string | null>(null);

  /* Reset on every (re)open so cancel+reopen restores the persisted values even
     when the flag object reference is unchanged. Without the `open` dependency
     the effect skips when reopening the same flag and the form shows abandoned
     edits from the previous session.

     Keyed on the flag KEY rather than the object: `update`'s failure path calls
     `loadFlags()`, which hands back a new object for the same flag. Depending on
     identity meant a rejected save re-ran this and wiped the very edits the
     operator was being asked to correct, while the dialog sat open telling them
     to fix something that was no longer on screen. */
  const flagKey = flag?.flagKey;
  useEffect(() => {
    if (open && flag) {
      setDescription(flag.description);
      setDefaultKey(flag.defaultVariationKey);
      setDefaultValueRaw(encodeValue(flag.defaultVariationValue));
      setKeyError(null);
      setValueError(null);
      setServeError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on flagKey by design; see above
  }, [open, flagKey]);

  /* Seed the serve fields from the existing rule once the rules arrive. An
     inert flag keeps the on/true default, which is what an operator reaching
     for this control almost always wants. */
  useEffect(() => {
    if (!open || rules === null) return;
    const state = classifyServeRule(rules);
    if (state.kind === "simple") {
      setServeKey(state.rule.variationKey);
      setServeValueRaw(encodeValue(state.rule.variationValue));
    } else {
      setServeKey("on");
      setServeValueRaw("true");
    }
  }, [open, rules]);

  if (!flag) return null;

  const serveState = rules === null ? null : classifyServeRule(rules);

  function handleConfirm(): void {
    if (!flag) return;
    const trimmedKey = defaultKey.trim();
    if (trimmedKey.length === 0) {
      setKeyError("Default variation key cannot be empty");
      return;
    }
    const decoded = decodeValue(defaultValueRaw);
    if (!decoded.ok) {
      setValueError('Must be valid JSON (e.g. true, false, "on", 42)');
      return;
    }
    const input: UpdateFlagInput = {
      description,
      defaultVariationKey: trimmedKey,
      defaultVariationValue: decoded.value,
    };

    /* The serve rule is only submitted when this form is allowed to own it.
       For a complex flag the fields are not rendered at all, so there is
       nothing to send and the targeting is left exactly as it was. */
    let serve: ServeValueEdit | null = null;
    if (!rulesError && serveState !== null && serveState.kind !== "complex") {
      const trimmedServeKey = serveKey.trim();
      if (trimmedServeKey.length === 0) {
        setServeError("Variation key cannot be empty");
        return;
      }
      const decodedServe = decodeValue(serveValueRaw);
      if (!decodedServe.ok) {
        setServeError('Must be valid JSON (e.g. true, false, "on", 42)');
        return;
      }
      serve = { variationKey: trimmedServeKey, variationValue: decodedServe.value };
    }

    onConfirm(flag.flagKey, input, serve);
  }

  return (
    <AlertDialog open={open}>
      <AlertDialogContent data-testid="edit-flag-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Edit {flag.flagKey}</AlertDialogTitle>
          <AlertDialogDescription>
            Change the description and the default variation returned when no rule matches. Use the
            per-environment toggles on the row to enable or disable this flag in a specific
            environment.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <label htmlFor="edit-flag-description" className="text-sm font-medium">
              Description
            </label>
            <Input
              id="edit-flag-description"
              data-testid="edit-flag-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label htmlFor="edit-flag-default-key" className="text-sm font-medium">
                Default variation key
              </label>
              <Input
                id="edit-flag-default-key"
                data-testid="edit-flag-default-key-input"
                placeholder="off"
                value={defaultKey}
                onChange={(e) => {
                  setDefaultKey(e.target.value);
                  setKeyError(null);
                }}
              />
              {keyError && <p className="text-xs text-destructive">{keyError}</p>}
            </div>
            <div className="space-y-2">
              <label htmlFor="edit-flag-default-value" className="text-sm font-medium">
                Default value (JSON)
              </label>
              <Input
                id="edit-flag-default-value"
                data-testid="edit-flag-default-value-input"
                placeholder="false"
                value={defaultValueRaw}
                onChange={(e) => {
                  setDefaultValueRaw(e.target.value);
                  setValueError(null);
                }}
              />
              {valueError && <p className="text-xs text-destructive">{valueError}</p>}
            </div>
          </div>
          {/* What the flag serves while enabled. Without a rule the evaluator
              has nothing to match and returns the default in every
              environment, so a flag can sit "enabled" and still serve `off`
              with no indication anywhere that it will never do otherwise. */}
          {rulesError ? (
            <div data-testid="edit-flag-rules-error" className="space-y-1 rounded-md border p-3">
              <p className="text-sm font-medium">Could not load this flag&apos;s rules</p>
              <p className="text-sm text-muted-foreground">
                What it serves when enabled cannot be shown or changed until they load. Close and
                reopen to try again. The description and default variation above still save.
              </p>
            </div>
          ) : serveState === null ? (
            <p data-testid="edit-flag-serve-loading" className="text-sm text-muted-foreground">
              Loading rules...
            </p>
          ) : serveState.kind === "complex" ? (
            <div data-testid="edit-flag-serve-complex" className="space-y-1 rounded-md border p-3">
              <p className="text-sm font-medium">Targeting rules are set on this flag</p>
              <p className="text-sm text-muted-foreground">
                {serveState.rules.length} rule{serveState.rules.length === 1 ? "" : "s"} with
                conditions, segments or a partial rollout. They are not editable here, and this form
                leaves them untouched. Change them through the flags API.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="space-y-1">
                <p className="text-sm font-medium">When enabled, serve</p>
                {serveState.kind === "inert" ? (
                  <p data-testid="edit-flag-serve-inert" className="text-sm text-muted-foreground">
                    This flag has no rule, so it serves its default variation in every environment
                    and the per-environment toggles do nothing. Saving adds the rule.
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <label htmlFor="edit-flag-serve-key" className="text-sm font-medium">
                    Variation key
                  </label>
                  <Input
                    id="edit-flag-serve-key"
                    data-testid="edit-flag-serve-key"
                    value={serveKey}
                    onChange={(e) => {
                      setServeKey(e.target.value);
                      setServeError(null);
                    }}
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="edit-flag-serve-value" className="text-sm font-medium">
                    Value (JSON)
                  </label>
                  <Input
                    id="edit-flag-serve-value"
                    data-testid="edit-flag-serve-value"
                    value={serveValueRaw}
                    onChange={(e) => {
                      setServeValueRaw(e.target.value);
                      setServeError(null);
                    }}
                  />
                </div>
              </div>
              {serveError ? (
                <p data-testid="edit-flag-serve-error" className="text-sm text-destructive">
                  {serveError}
                </p>
              ) : null}
            </div>
          )}
          {saveError ? (
            <p data-testid="edit-flag-save-error" className="text-sm text-destructive">
              {saveError}
            </p>
          ) : null}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="edit-flag-cancel" onClick={onCancel} disabled={saving}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="edit-flag-confirm"
            onClick={handleConfirm}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
